export interface Bank {
  id: string;
  name: string;
  shortName: string;
  country: string;
  domain: string;
  logo?: string;
}

export const COUNTRY_TO_CURRENCY: Record<string, string> = {
  FR: 'EUR',
  BE: 'EUR',
  ES: 'EUR',
  DE: 'EUR',
  IT: 'EUR',
  NL: 'EUR',
  PT: 'EUR',
  AT: 'EUR',
  IE: 'EUR',
  FI: 'EUR',
  LU: 'EUR',
  MA: 'MAD',
  US: 'USD',
  CA: 'CAD',
};

export function getCurrencyForCountry(countryCode: string): string {
  return COUNTRY_TO_CURRENCY[countryCode.toUpperCase()] || 'EUR';
}

export const COUNTRY_NAMES: Record<string, { en: string; fr: string }> = {
  MA: { en: 'Morocco', fr: 'Maroc' },
  FR: { en: 'France', fr: 'France' },
  BE: { en: 'Belgium', fr: 'Belgique' },
  ES: { en: 'Spain', fr: 'Espagne' },
  DE: { en: 'Germany', fr: 'Allemagne' },
  CA: { en: 'Canada', fr: 'Canada' },
};

export const COUNTRY_FLAGS: Record<string, string> = {
  MA: 'ma',
  FR: 'fr',
  BE: 'be',
  ES: 'es',
  DE: 'de',
  CA: 'ca',
};

export function getCountryFlagUrl(countryCode: string): string {
  const code = COUNTRY_FLAGS[countryCode] || countryCode.toLowerCase();
  return `https://flagcdn.com/${code}.svg`;
}

export const BANKS: Bank[] = [
  { id: '68', name: 'AXA Bank Belgium', shortName: 'AXA Bank', country: 'BE', domain: 'axa.be' },
  {
    id: '71',
    name: "Argenta Banque d'Épargne",
    shortName: 'Argenta',
    country: 'BE',
    domain: 'argenta.be',
  },
  {
    id: '64',
    name: 'BNP Paribas Fortis',
    shortName: 'BNP Paribas Fortis',
    country: 'BE',
    domain: 'bnpparibasfortis.be',
  },
  {
    id: '73',
    name: 'Banque Nagelmackers',
    shortName: 'Nagelmackers',
    country: 'BE',
    domain: 'nagelmackers.be',
  },
  {
    id: '74',
    name: 'Banque Van Breda',
    shortName: 'Van Breda',
    country: 'BE',
    domain: 'banquevanbreda.be',
  },
  { id: '66', name: 'Belfius Banque', shortName: 'Belfius', country: 'BE', domain: 'belfius.be' },
  { id: '69', name: 'CBC Banque', shortName: 'CBC', country: 'BE', domain: 'cbc.be' },
  { id: '70', name: 'Crelan', shortName: 'Crelan', country: 'BE', domain: 'crelan.be' },
  {
    id: '76',
    name: 'Hello bank! Belgium',
    shortName: 'Hello bank!',
    country: 'BE',
    domain: 'hellobank.be',
  },
  { id: '67', name: 'ING Belgique', shortName: 'ING Belgique', country: 'BE', domain: 'ing.be' },
  { id: '65', name: 'KBC Bank', shortName: 'KBC', country: 'BE', domain: 'kbc.com' },
  {
    id: '75',
    name: 'Keytrade Bank',
    shortName: 'Keytrade',
    country: 'BE',
    domain: 'keytradebank.be',
  },
  { id: '94', name: 'Bank of Montreal', shortName: 'BMO', country: 'CA', domain: 'bmo.com' },
  {
    id: '93',
    name: 'Bank of Nova Scotia',
    shortName: 'Scotiabank',
    country: 'CA',
    domain: 'scotiabank.com',
  },
  {
    id: '95',
    name: 'Canadian Imperial Bank of Commerce',
    shortName: 'CIBC',
    country: 'CA',
    domain: 'cibc.com',
  },
  {
    id: '97',
    name: 'Desjardins Group',
    shortName: 'Desjardins',
    country: 'CA',
    domain: 'desjardins.com',
  },
  {
    id: '102',
    name: 'EQ Bank (Equitable Bank)',
    shortName: 'EQ Bank',
    country: 'CA',
    domain: 'eqbank.ca',
  },
  {
    id: '99',
    name: 'HSBC Bank Canada',
    shortName: 'HSBC Canada',
    country: 'CA',
    domain: 'hsbc.ca',
    logo: 'https://t1.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=https://www.hsbc.com&size=128',
  },
  {
    id: '98',
    name: 'Laurentian Bank of Canada',
    shortName: 'Laurentian Bank',
    country: 'CA',
    domain: 'laurentianbank.ca',
  },
  { id: '103', name: 'Motusbank', shortName: 'motusbank', country: 'CA', domain: 'motusbank.ca' },
  {
    id: '96',
    name: 'National Bank of Canada',
    shortName: 'National Bank',
    country: 'CA',
    domain: 'nbc.ca',
  },
  {
    id: '91',
    name: 'Royal Bank of Canada',
    shortName: 'RBC',
    country: 'CA',
    domain: 'rbcroyalbank.com',
  },
  {
    id: '101',
    name: 'Simplii Financial',
    shortName: 'Simplii',
    country: 'CA',
    domain: 'simplii.com',
  },
  {
    id: '100',
    name: 'Tangerine Bank',
    shortName: 'Tangerine',
    country: 'CA',
    domain: 'tangerine.ca',
  },
  { id: '92', name: 'Toronto-Dominion Bank', shortName: 'TD', country: 'CA', domain: 'td.com' },
  {
    id: '78',
    name: 'Commerzbank AG',
    shortName: 'Commerzbank',
    country: 'DE',
    domain: 'commerzbank.de',
  },
  {
    id: '88',
    name: 'Consorsbank',
    shortName: 'Consorsbank',
    country: 'DE',
    domain: 'consorsbank.de',
  },
  {
    id: '87',
    name: 'DKB Deutsche Kreditbank AG',
    shortName: 'DKB',
    country: 'DE',
    domain: 'dkb.de',
  },
  {
    id: '77',
    name: 'Deutsche Bank AG',
    shortName: 'Deutsche Bank',
    country: 'DE',
    domain: 'db.com',
  },
  {
    id: '79',
    name: 'HypoVereinsbank',
    shortName: 'HypoVereinsbank',
    country: 'DE',
    domain: 'hypovereinsbank.de',
  },
  { id: '86', name: 'ING Deutschland', shortName: 'ING', country: 'DE', domain: 'ing.de' },
  { id: '90', name: 'N26 Bank AG', shortName: 'N26', country: 'DE', domain: 'n26.com' },
  { id: '80', name: 'Postbank', shortName: 'Postbank', country: 'DE', domain: 'postbank.de' },
  {
    id: '82',
    name: 'Santander Consumer Bank AG',
    shortName: 'Santander DE',
    country: 'DE',
    domain: 'santander.de',
  },
  { id: '85', name: 'Sparda-Banken', shortName: 'Sparda-Bank', country: 'DE', domain: 'sparda.de' },
  {
    id: '83',
    name: 'Sparkassen-Finanzgruppe',
    shortName: 'Sparkasse',
    country: 'DE',
    domain: 'sparkasse.de',
  },
  { id: '81', name: 'TARGOBANK AG', shortName: 'Targobank', country: 'DE', domain: 'targobank.de' },
  {
    id: '84',
    name: 'Volksbanken Raiffeisenbanken',
    shortName: 'Volksbank Raiffeisenbank',
    country: 'DE',
    domain: 'vr.de',
  },
  { id: '89', name: 'comdirect', shortName: 'comdirect', country: 'DE', domain: 'comdirect.de' },
  {
    id: '56',
    name: 'ABANCA Corporación Bancaria',
    shortName: 'ABANCA',
    country: 'ES',
    domain: 'abanca.com',
  },
  {
    id: '50',
    name: 'Banco Bilbao Vizcaya Argentaria',
    shortName: 'BBVA',
    country: 'ES',
    domain: 'bbva.es',
  },
  {
    id: '52',
    name: 'Banco Sabadell',
    shortName: 'Sabadell',
    country: 'ES',
    domain: 'bancsabadell.com',
  },
  {
    id: '49',
    name: 'Banco Santander',
    shortName: 'Santander',
    country: 'ES',
    domain: 'bancosantander.es',
  },
  { id: '53', name: 'Bankinter', shortName: 'Bankinter', country: 'ES', domain: 'bankinter.com' },
  { id: '51', name: 'CaixaBank', shortName: 'CaixaBank', country: 'ES', domain: 'caixabank.es' },
  {
    id: '58',
    name: 'Caja Rural',
    shortName: 'Caja Rural',
    country: 'ES',
    domain: 'grupocajarural.es',
  },
  {
    id: '59',
    name: 'Deutsche Bank SAE',
    shortName: 'Deutsche Bank ES',
    country: 'ES',
    domain: 'deutsche-bank.es',
  },
  { id: '62', name: 'EVO Banco', shortName: 'EVO Banco', country: 'ES', domain: 'bankinter.com' },
  { id: '63', name: 'ING España', shortName: 'ING', country: 'ES', domain: 'ing.es' },
  { id: '57', name: 'Ibercaja Banco', shortName: 'Ibercaja', country: 'ES', domain: 'ibercaja.es' },
  { id: '61', name: 'Imagin', shortName: 'imagin', country: 'ES', domain: 'imagin.com' },
  {
    id: '55',
    name: 'Kutxabank',
    shortName: 'Kutxabank',
    country: 'ES',
    domain: 'kutxabank.es',
  },
  { id: '60', name: 'Openbank', shortName: 'Openbank', country: 'ES', domain: 'openbank.es' },
  {
    id: '54',
    name: 'Unicaja Banco',
    shortName: 'Unicaja',
    country: 'ES',
    domain: 'unicajabanco.es',
  },
  { id: '33', name: 'AXA Banque', shortName: 'AXA Banque', country: 'FR', domain: 'axa.fr' },
  {
    id: '24',
    name: 'BNP Paribas',
    shortName: 'BNP Paribas',
    country: 'FR',
    domain: 'mabanque.bnpparibas',
  },
  {
    id: '36',
    name: 'Banque Palatine',
    shortName: 'Banque Palatine',
    country: 'FR',
    domain: 'palatine.fr',
  },
  {
    id: '27',
    name: 'Banque Populaire',
    shortName: 'Banque Populaire',
    country: 'FR',
    domain: 'banquepopulaire.fr',
  },
  {
    id: '38',
    name: 'Boursorama Banque',
    shortName: 'Boursorama',
    country: 'FR',
    domain: 'boursobank.com',
  },
  { id: '30', name: 'CIC', shortName: 'CIC', country: 'FR', domain: 'cic.fr' },
  {
    id: '28',
    name: "Caisse d'Epargne",
    shortName: "Caisse d'Epargne",
    country: 'FR',
    domain: 'caisse-epargne.fr',
  },
  {
    id: '26',
    name: 'Crédit Agricole',
    shortName: 'Crédit Agricole',
    country: 'FR',
    domain: 'credit-agricole.fr',
  },
  {
    id: '35',
    name: 'Crédit Coopératif',
    shortName: 'Crédit Coopératif',
    country: 'FR',
    domain: 'credit-cooperatif.coop',
  },
  {
    id: '29',
    name: 'Crédit Mutuel',
    shortName: 'Crédit Mutuel',
    country: 'FR',
    domain: 'creditmutuel.fr',
  },
  {
    id: '40',
    name: 'Fortuneo Banque',
    shortName: 'Fortuneo',
    country: 'FR',
    domain: 'fortuneo.fr',
  },
  {
    id: '34',
    name: 'HSBC Continental Europe',
    shortName: 'HSBC',
    country: 'FR',
    domain: 'hsbc.fr',
    logo: 'https://t1.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=https://www.hsbc.com&size=128',
  },
  {
    id: '39',
    name: 'Hello bank!',
    shortName: 'Hello bank!',
    country: 'FR',
    domain: 'hellobank.fr',
  },
  { id: '32', name: 'LCL Banque et Assurance', shortName: 'LCL', country: 'FR', domain: 'lcl.fr' },
  {
    id: '31',
    name: 'La Banque Postale',
    shortName: 'La Banque Postale',
    country: 'FR',
    domain: 'labanquepostale.fr',
  },
  { id: '47', name: 'Lydia', shortName: 'Lydia', country: 'FR', domain: 'lydia.me' },
  {
    id: '42',
    name: 'Ma French Bank',
    shortName: 'Ma French Bank',
    country: 'FR',
    domain: 'labanquepostale.fr',
  },
  { id: '37', name: 'Milleis Banque', shortName: 'Milleis', country: 'FR', domain: 'milleis.fr' },
  { id: '41', name: 'Monabanq', shortName: 'Monabanq', country: 'FR', domain: 'monabanq.com' },
  { id: '45', name: 'N26', shortName: 'N26', country: 'FR', domain: 'n26.com' },
  { id: '46', name: 'Nickel', shortName: 'Nickel', country: 'FR', domain: 'nickel.eu' },
  {
    id: '43',
    name: 'Orange Bank',
    shortName: 'Orange Bank',
    country: 'FR',
    domain: 'orangeobk.fr',
  },
  { id: '44', name: 'Revolut', shortName: 'Revolut', country: 'FR', domain: 'revolut.com' },
  {
    id: '25',
    name: 'Société Générale',
    shortName: 'Société Générale',
    country: 'FR',
    domain: 'particuliers.sg.fr',
  },
  { id: '48', name: 'bunq', shortName: 'bunq', country: 'FR', domain: 'bunq.com' },
  {
    id: '23',
    name: 'Al Akhdar Bank',
    shortName: 'Al Akhdar Bank',
    country: 'MA',
    domain: 'creditagricole.ma',
  },
  { id: '9', name: 'Al Barid Bank', shortName: 'ABB', country: 'MA', domain: 'albaridbank.ma' },
  {
    id: '11',
    name: 'Arab Bank Maroc',
    shortName: 'Arab Bank',
    country: 'MA',
    domain: 'arabbank.ma',
  },
  {
    id: '1',
    name: 'Attijariwafa Bank',
    shortName: 'AWB',
    country: 'MA',
    domain: 'attijariwafabank.com',
  },
  { id: '20', name: 'BTI Bank', shortName: 'BTI Bank', country: 'MA', domain: 'ocapitalgroup.ma' },
  {
    id: '21',
    name: 'Bank Al Yousr',
    shortName: 'Bank Al Yousr',
    country: 'MA',
    domain: 'alyousr.ma',
  },
  {
    id: '22',
    name: 'Bank Assafa',
    shortName: 'Bank Assafa',
    country: 'MA',
    domain: 'bankassafa.com',
  },
  { id: '3', name: 'Bank of Africa', shortName: 'BOA', country: 'MA', domain: 'bankofafrica.ma' },
  {
    id: '2',
    name: 'Banque Centrale Populaire',
    shortName: 'BCP',
    country: 'MA',
    domain: 'groupebcp.com',
  },
  {
    id: '7',
    name: "Banque Marocaine pour le Commerce et l'Industrie",
    shortName: 'BMCI',
    country: 'MA',
    domain: 'bmci.ma',
  },
  { id: '10', name: 'CFG Bank', shortName: 'CFG', country: 'MA', domain: 'cfgbank.com' },
  { id: '4', name: 'CIH Bank', shortName: 'CIH', country: 'MA', domain: 'cihbank.ma' },
  { id: '14', name: 'Citibank Morocco', shortName: 'Citibank', country: 'MA', domain: 'citi.com' },
  {
    id: '5',
    name: 'Crédit Agricole du Maroc',
    shortName: 'CAM',
    country: 'MA',
    domain: 'creditagricole.ma',
  },
  { id: '6', name: 'Crédit du Maroc', shortName: 'CDM', country: 'MA', domain: 'creditdumaroc.ma' },
  { id: '8', name: 'Saham bank', shortName: 'SB', country: 'MA', domain: 'sahambank.com' },
  {
    id: '19',
    name: 'Umnia Bank',
    shortName: 'Umnia Bank',
    country: 'MA',
    domain: 'umniabank.ma',
    logo: '/bank-logos/umniabank_ma.ico',
  },
];

export function getBankLogoUrl(bank: { domain: string; logo?: string }): string {
  if (bank.logo) return bank.logo;
  if (!bank.domain) return '';
  const filename = bank.domain.replace(/\./g, '_');
  return `/bank-logos/${filename}.png`;
}

// ---------------------------------------------------------------------------
// Finding a bank from the name an aggregator gives us
// ---------------------------------------------------------------------------

/**
 * Comparable form of a bank name: no case, no accents, no punctuation.
 * "Société Générale" and "societe generale" become the same string.
 */
export function normalizeBankName(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
}

/**
 * Same, but as words: "Caisse d'Épargne" becomes "caisse d epargne".
 *
 * Matching a name inside a longer one has to happen on whole words. On raw
 * letters, "Ma banque imaginaire" contains "imagin" and would come back as the
 * Spanish bank Imagin.
 */
function bankNameWords(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/** name beats shortName beats domain, so an exact match never picks a weaker one. */
const enum KeyRank {
  Name = 0,
  ShortName = 1,
  Domain = 2,
}

interface BankKey {
  bank: Bank;
  rank: KeyRank;
  order: number;
}

/** Below this, a name is initials that say nothing on their own ("SB"). */
const MIN_CONTAINED_KEY = 3;

interface BankIndex {
  /** Letters only, for an exact answer: "boursobank", "societegenerale". */
  compact: Map<string, BankKey[]>;
  /** Words, for a name sitting inside a longer one: "credit agricole". */
  words: Map<string, BankKey[]>;
}

const BANK_INDEX: BankIndex = (() => {
  const compact = new Map<string, BankKey[]>();
  const words = new Map<string, BankKey[]>();

  const add = (map: Map<string, BankKey[]>, key: string, entry: BankKey) => {
    if (!key) return;
    const existing = map.get(key);
    if (!existing) {
      map.set(key, [entry]);
    } else if (!existing.some((e) => e.bank.id === entry.bank.id)) {
      existing.push(entry);
    }
  };

  BANKS.forEach((bank, order) => {
    const sources: [string, KeyRank][] = [
      [bank.name, KeyRank.Name],
      [bank.shortName, KeyRank.ShortName],
      // The first label of the domain: it is what recognises a bank the
      // aggregator names after its app rather than its company — Powens says
      // "BoursoBank" where this list says "Boursorama Banque".
      [bank.domain.split('.')[0] ?? '', KeyRank.Domain],
    ];

    for (const [raw, rank] of sources) {
      const entry = { bank, rank, order };
      add(compact, normalizeBankName(raw ?? ''), entry);
      // Domains never take part in partial matching: some are shared
      // ("creditagricole.ma" serves two Moroccan banks) and some are generic
      // ("particuliers.sg.fr" would swallow every "… Particuliers" connector).
      if (rank !== KeyRank.Domain) add(words, bankNameWords(raw ?? ''), entry);
    }
  });

  return { compact, words };
})();

/** The best of several banks sharing a key: strongest kind of key, then list order. */
function pick(entries: BankKey[], country?: string): Bank {
  const preferred = country ? entries.filter((e) => e.bank.country === country) : [];
  const pool = preferred.length > 0 ? preferred : entries;
  return [...pool].sort((a, b) => a.rank - b.rank || a.order - b.order)[0].bank;
}

/**
 * The bank behind an institution name, however it is spelled.
 *
 * Aggregated accounts carry the name Powens gives the connector, which rarely
 * matches this list word for word: "Fortuneo" against "Fortuneo Banque",
 * "BoursoBank" against "Boursorama Banque", "Crédit Agricole - Particuliers"
 * against "Crédit Agricole". Comparing names literally left every aggregated
 * account without its logo.
 *
 * Partial matching is on whole words only, and never on a domain: it answers
 * nothing rather than answering wrongly.
 */
export function findBankByName(
  institution: string | null | undefined,
  country?: string
): Bank | undefined {
  if (!institution) return undefined;

  const exactName = BANKS.find((bank) => bank.name === institution);
  if (exactName) return exactName;

  const compact = normalizeBankName(institution);
  if (!compact) return undefined;

  const exact = BANK_INDEX.compact.get(compact);
  if (exact) return pick(exact, country);

  const words = ` ${bankNameWords(institution)} `;
  let best: BankKey[] | undefined;
  let bestLength = 0;
  for (const [key, entries] of BANK_INDEX.words) {
    if (key.length < MIN_CONTAINED_KEY || key.length <= bestLength) continue;
    if (!words.includes(` ${key} `)) continue;
    best = entries;
    bestLength = key.length;
  }

  return best ? pick(best, country) : undefined;
}
